import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiLogOut } from 'react-icons/fi';
import LanguageSelector from '../ui/LanguageSelector';

interface DashboardHeaderProps {
  userName?: string;
  userEmail?: string;
  photoUrl?: string;
  firstName?: string;
  lastName?: string;
  accentColor?: string;
  onSignOut: () => void;
}

export default function DashboardHeader({
  userName,
  userEmail,
  photoUrl,
  firstName,
  lastName,
  accentColor,
  onSignOut,
}: DashboardHeaderProps) {
  const { t } = useTranslation();

  return (
    <header className="relative z-10 bg-gray-900">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/4 w-64 h-64 bg-mk-pink-500/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/4 w-64 h-64 bg-mk-pink-500/5 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <h1 className="text-xl font-bold">
            <span className="text-white">MyPink</span>
            <span className="text-mk-pink-400">Contact</span>
          </h1>

          {/* Right side */}
          <div className="flex items-center gap-4">
            <LanguageSelector variant="light" />

            {/* User profile - navigates to settings */}
            <Link
              to="/settings"
              className="flex items-center gap-3 hover:bg-gray-800/50 rounded-lg px-3 py-2 -mr-3 transition-colors"
              title={t('settings.title')}
            >
              {/* User info - hidden on mobile */}
              <div className="hidden sm:block text-right">
                {userName && (
                  <p className="text-sm font-medium text-white">{userName}</p>
                )}
                <p className="text-xs text-gray-400">{userEmail}</p>
              </div>
              {/* Avatar */}
              <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-white/20">
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={userName || 'User'}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div
                    className="w-full h-full flex items-center justify-center text-white text-sm font-bold"
                    style={{ backgroundColor: accentColor || '#e91e8c' }}
                  >
                    {(firstName?.[0] || '').toUpperCase()}
                    {(lastName?.[0] || '').toUpperCase()}
                  </div>
                )}
              </div>
            </Link>

            {/* Sign out button */}
            <button
              onClick={onSignOut}
              className="p-2 text-gray-400 hover:text-white transition-colors"
              title={t('nav.signOut')}
            >
              <FiLogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}
